/**
 * Genera el PDF de una Cotización (carta completa o media carta).
 * Recibe el documento `quote` persistido (folio, nombre, telefono, fecha_evento,
 * salon, zona, factura, servicios:{cabina,video360}, total) y `pdfTextos`
 * (textos editables desde Admin: flete, galería/privacidad, notas, link de
 * privacidad, pie de página, más `nota_precios` tomado de Admin → Precios).
 * Usado tanto por el /cotizador público como por el Admin (Ver PDF / Editar).
 */
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { PICPARTY_LOGO_BASE64 } from "../../../lib/picpartyLogo";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(amount || 0);

const formatEventDate = (isoDate) => {
  if (!isoDate) return "";
  try {
    const d = new Date(isoDate + "T12:00:00");
    const weekday = d.toLocaleDateString("es-MX", { weekday: "short" }).replace(".", "");
    const month = d.toLocaleDateString("es-MX", { month: "short" }).replace(".", "");
    return `${weekday} ${d.getDate()} ${month} ${d.getFullYear()}`;
  } catch {
    return isoDate;
  }
};

const toLocalDateStr = (date) => {
  const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, "0"), d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const generateQuotePDF = async (quote, pdfTextos, pdfFormat = "letter") => {
  const servicios = quote.servicios || {};
  const cabina = servicios.cabina || {};
  const video360 = servicios.video360 || {};

  let qrDataUrl = null;
  try {
    const QRCode = (await import("qrcode")).default;
    qrDataUrl = await QRCode.toDataURL(pdfTextos.privacy_url, { width: 300, margin: 1, errorCorrectionLevel: "M", color: { dark: "#000000", light: "#FFFFFF" } });
  } catch (e) {
    console.error("Error generando QR del aviso de privacidad:", e);
  }

  const isHalfLetter = pdfFormat === "half-letter";
  const pageFormat = isHalfLetter ? [139.7, 215.9] : "letter";
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: pageFormat });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = isHalfLetter ? 10 : 20;

  const headerHeight = isHalfLetter ? 32 : 50;
  pdf.setFillColor(245, 245, 245);
  pdf.rect(0, 0, pageWidth, headerHeight, "F");
  try {
    const logoSize = isHalfLetter ? 26 : 40;
    pdf.addImage(PICPARTY_LOGO_BASE64, "PNG", margin - 8, 3, logoSize, logoSize);
  } catch (e) {
    console.error("Error cargando logo cotización:", e);
  }

  pdf.setFontSize(isHalfLetter ? 18 : 28);
  pdf.setTextColor(30, 30, 30);
  pdf.setFont(undefined, "bold");
  pdf.text("COTIZACIÓN", pageWidth - margin, isHalfLetter ? 16 : 25, { align: "right" });
  pdf.setFontSize(isHalfLetter ? 9 : 12);
  pdf.setTextColor(80, 80, 80);
  pdf.setFont(undefined, "normal");
  pdf.text(`Folio: ${quote.folio}`, pageWidth - margin, isHalfLetter ? 26 : 38, { align: "right" });

  const qrSize = 25;
  let y = headerHeight + (isHalfLetter ? 6 : 10);
  const qrTop = y;
  if (qrDataUrl) {
    try {
      pdf.addImage(qrDataUrl, "PNG", pageWidth - margin - qrSize, y, qrSize, qrSize);
      pdf.setFontSize(isHalfLetter ? 6 : 7);
      pdf.setTextColor(120, 120, 120);
      pdf.setFont(undefined, "normal");
      pdf.text("Aviso de privacidad", pageWidth - margin - qrSize / 2, y + qrSize + 4, { align: "center" });
    } catch (e) {
      console.error("Error insertando QR en PDF:", e);
    }
  }

  pdf.setFontSize(isHalfLetter ? 10 : 14);
  pdf.setTextColor(30, 30, 30);
  pdf.setFont(undefined, "bold");
  pdf.text("DATOS DEL CLIENTE", margin, y);
  y += isHalfLetter ? 5 : 8;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(isHalfLetter ? 8 : 11);
  pdf.setTextColor(50, 50, 50);
  pdf.text(`Nombre: ${quote.nombre || ""}`, margin, y); y += isHalfLetter ? 4 : 6;
  pdf.text(`Fecha de cotización: ${formatEventDate(toLocalDateStr(quote.created_at ? new Date(quote.created_at) : new Date()))}`, margin, y); y += isHalfLetter ? 4 : 6;
  if (quote.telefono) { pdf.text(`Teléfono: ${quote.telefono}`, margin, y); y += isHalfLetter ? 4 : 6; }
  if (quote.salon) { pdf.text(`Lugar/Salón: ${quote.salon}`, margin, y); y += isHalfLetter ? 4 : 6; }
  if (quote.fecha_evento) { pdf.text(`Fecha del Evento: ${formatEventDate(quote.fecha_evento)}`, margin, y); y += isHalfLetter ? 4 : 6; }
  pdf.text(`Factura: ${quote.factura ? "Sí (el total no cambia, ya incluye IVA)" : "No"}`, margin, y); y += isHalfLetter ? 4 : 6;

  y = Math.max(y, qrTop + qrSize + (isHalfLetter ? 10 : 13));

  // Flete
  y += isHalfLetter ? 2 : 4;
  const fleteTitle = quote.zona === "foraneo" ? pdfTextos.flete_foraneo_titulo : pdfTextos.flete_local_titulo;
  const fleteTexto = quote.zona === "foraneo" ? pdfTextos.flete_foraneo_texto : pdfTextos.flete_local_texto;
  pdf.setFont(undefined, "bold");
  pdf.setFontSize(isHalfLetter ? 9 : 11);
  pdf.setTextColor(30, 30, 30);
  pdf.text(fleteTitle, margin, y); y += isHalfLetter ? 4 : 6;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(isHalfLetter ? 7.5 : 9.5);
  pdf.setTextColor(60, 60, 60);
  const fleteLines = pdf.splitTextToSize(fleteTexto, pageWidth - margin * 2);
  pdf.text(fleteLines, margin, y); y += fleteLines.length * (isHalfLetter ? 3.6 : 4.6);
  pdf.setFontSize(isHalfLetter ? 6.5 : 8);
  pdf.setTextColor(120, 120, 120);
  const fleteExtraLines = pdf.splitTextToSize(pdfTextos.flete_extra_texto, pageWidth - margin * 2);
  pdf.text(fleteExtraLines, margin, y); y += fleteExtraLines.length * (isHalfLetter ? 3.2 : 4);

  y += isHalfLetter ? 4 : 7;
  pdf.setFontSize(isHalfLetter ? 10 : 14);
  pdf.setTextColor(30, 30, 30);
  pdf.setFont(undefined, "bold");
  pdf.text("DETALLE DE SERVICIOS", margin, y);
  y += isHalfLetter ? 6 : 10;

  pdf.setFillColor(240, 240, 240);
  pdf.rect(margin, y - (isHalfLetter ? 3 : 5), pageWidth - margin * 2, isHalfLetter ? 5 : 8, "F");
  pdf.setFontSize(isHalfLetter ? 7 : 10);
  pdf.setFont(undefined, "bold");
  pdf.setTextColor(50, 50, 50);
  pdf.text("Concepto", margin + 5, y);
  pdf.text("Precio Neto", pageWidth - margin - 5, y, { align: "right" });
  y += isHalfLetter ? 6 : 10;

  pdf.setFont(undefined, "normal");
  const lineSpacing = isHalfLetter ? 5 : 7;

  if (cabina.selected) {
    pdf.text(cabina.label || "Cabina de Fotos", margin + 5, y);
    pdf.text(formatCurrency(cabina.precio), pageWidth - margin - 5, y, { align: "right" });
    y += lineSpacing;
  }
  if (video360.selected) {
    pdf.text(video360.label || "Video 360°", margin + 5, y);
    pdf.text(formatCurrency(video360.precio), pageWidth - margin - 5, y, { align: "right" });
    y += lineSpacing;
  }

  y += isHalfLetter ? 3 : 5;
  pdf.setDrawColor(180, 180, 180);
  pdf.line(margin, y, pageWidth - margin, y);
  y += isHalfLetter ? 5 : 8;

  const totalBoxHeight = isHalfLetter ? 8 : 12;
  pdf.setFillColor(60, 60, 60);
  pdf.rect(margin, y - (isHalfLetter ? 3 : 5), pageWidth - margin * 2, totalBoxHeight, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(isHalfLetter ? 10 : 14);
  pdf.setFont(undefined, "bold");
  pdf.text("TOTAL NETO:", margin + 5, y + (isHalfLetter ? 1 : 3));
  pdf.text(formatCurrency(quote.total), pageWidth - margin - 5, y + (isHalfLetter ? 1 : 3), { align: "right" });
  y += totalBoxHeight;

  y += isHalfLetter ? 4 : 6;
  pdf.setFontSize(isHalfLetter ? 7 : 9);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont(undefined, "normal");
  pdf.text(pdfTextos.nota_precios || "", margin, y);

  // Galería y privacidad
  y += isHalfLetter ? 8 : 12;
  if (y > pageHeight - (isHalfLetter ? 55 : 75)) { pdf.addPage(); y = margin; }
  pdf.setFontSize(isHalfLetter ? 9 : 12);
  pdf.setTextColor(30, 30, 30);
  pdf.setFont(undefined, "bold");
  pdf.text(pdfTextos.galeria_titulo, margin, y); y += isHalfLetter ? 4 : 7;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(isHalfLetter ? 6.5 : 8.5);
  pdf.setTextColor(70, 70, 70);
  const galeriaLines = pdf.splitTextToSize(pdfTextos.galeria_texto, pageWidth - margin * 2);
  pdf.text(galeriaLines, margin, y);
  y += galeriaLines.length * (isHalfLetter ? 3.2 : 4.2);

  // Footer con notas legales + link del aviso de privacidad
  y += isHalfLetter ? 6 : 10;
  const footerBlockHeight = isHalfLetter ? 32 : 40;
  if (y > pageHeight - footerBlockHeight) { pdf.addPage(); y = margin; }
  pdf.setFontSize(isHalfLetter ? 6 : 8);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont(undefined, "italic");
  const footerSpacing = isHalfLetter ? 4 : 5;
  pdf.text(pdfTextos.nota_vigencia, margin, y); y += footerSpacing;
  pdf.text(pdfTextos.nota_flete_legal, margin, y); y += footerSpacing;
  pdf.text(pdfTextos.nota_cambios, margin, y); y += footerSpacing;
  pdf.setFont(undefined, "bold");
  pdf.text(pdfTextos.nota_no_contrato, margin, y); y += footerSpacing + 2;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(isHalfLetter ? 5.5 : 7.5);
  pdf.setTextColor(90, 90, 90);
  const privacyLines = pdf.splitTextToSize(`Aviso de privacidad: ${pdfTextos.privacy_url}`, pageWidth - margin * 2);
  pdf.text(privacyLines, margin, y);
  y += privacyLines.length * (isHalfLetter ? 3 : 4) + 2;
  pdf.setFontSize(isHalfLetter ? 7 : 9);
  pdf.setTextColor(150, 150, 150);
  pdf.text(pdfTextos.pie_pagina, pageWidth / 2, y, { align: "center" });

  const formatSuffix = isHalfLetter ? "_mediaCarta" : "";
  const fileName = `Cotizacion_${quote.folio}${formatSuffix}.pdf`;
  const pdfBlob = pdf.output("blob");
  const blobUrl = URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
  toast.success(`PDF ${isHalfLetter ? "media carta" : "carta completa"} descargado`);
};
